import React, { useState, useEffect, useMemo } from 'react';
import MatchCard from "../../MatchCard/MatchCard";
import SectionTitle from "../../SectionTitle/SectionTitle";
import './styles.scss';
import firstrectangle from '../../../assets/firstrectangle.png';
import secondrectangle from '../../../assets/secondrectangle.png';
import thirdrectangle from '../../../assets/thirdrectangle.png';
import bigShield from '../../../assets/bigShield.png';
import mediumShield from '../../../assets/mediumShield.png';
import smallShield from '../../../assets/smallShield.png';
import { useTranslation } from 'hooks/useTranslation';
import { TRANSLATION_KEYS } from '../GroupStage/constants';
import { formatMatchTimeByRegion } from '../../../../utils/formatMatchTime';
import { getRegion } from 'utils/Region';
import { useMundialMetrics } from '../../../../hooks/useMundialMetrics';
import { INTERACTION_TYPES } from '../../../../hooks/useMundialMetrics/constants';
import { ACTIONABLE_MATCH_STATUSES } from '../../../../hooks/useMundialMetrics/utils/buildMatchValues';
import { useMundialScrollTracker } from '../../../../hooks/useMundialScrollTracker';
import { getVisibleMatchNames } from '../../../utils/getVisibleMatchNames';

const FINAL_MOBILE_MEDIA = '(max-width: 1024px)';



export default function FinalStage({ stageData, thirdPlaceData, onMatchCardClick }) {
useMundialScrollTracker({
    pageTitleSegments: ['final'],
    contentSubsection: 'final',
    moduloName: '',
    getVisibleContentIds: getVisibleMatchNames,
  });

  const { trackSelectContent } = useMundialMetrics();

  const handleMatchClick = (matchData) => {
    onMatchCardClick?.(matchData.matchId);
    if (!ACTIONABLE_MATCH_STATUSES.includes(matchData.status)) return;
    const matchName = `${matchData.homeTeam?.name || ''} vs ${matchData.awayTeam?.name || ''}`;
    trackSelectContent({
      pageTitleSegments: ['final'],
      pageTitle: matchName,
      contentSubsection: 'final',
      moduloName: '',
      interactionType: INTERACTION_TYPES.CARD_SELECT,
      match: matchData,
    });
  };

  const { translationMapper } = useTranslation();
      const translatedTexts = useMemo(
          () => translationMapper(TRANSLATION_KEYS),
          [],
        );

        const PODIUM = [
  { label: translatedTexts['SECOND_PLACE'], placeKey: "second", height: 32 },
  { label: translatedTexts['FIRST_PLACE'], placeKey: "first", height: 64 },
  { label: translatedTexts['THIRD_PLACE'], placeKey: "third", height: 16 },
];

  const match = stageData?.data?.rounds?.[0]?.matches?.[0] || null;
  const stageName = translatedTexts['TITLE_1_FINAL'];

  const [isFinalMobileLayout, setIsFinalMobileLayout] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(FINAL_MOBILE_MEDIA).matches : false
  );

  const formatTime = m =>
    formatMatchTimeByRegion(m, {
      includeDate: !isFinalMobileLayout,
      bracketSingleLine: true,
    });

  const formatDate = (m) => {
    if (!m?.scheduledAt) return '';
    const d = new Date(m.scheduledAt);
    const day = d.getDate();
    const month = d.toLocaleString('es-MX', { month: 'long' }).toUpperCase();
    return `${day} DE ${month}`;
  };

  useEffect(() => {
    const mq = window.matchMedia(FINAL_MOBILE_MEDIA);
    const sync = () => setIsFinalMobileLayout(mq.matches);
    sync();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  const matchCardNoFlagSrc = isFinalMobileLayout ? smallShield : mediumShield;

  const isCompleted = match?.status === 'COMPLETED';
  const winner = isCompleted && match?.score
    ? (match.score.home > match.score.away ? match.homeTeam : match.awayTeam)
    : null;
  const loser = isCompleted && match?.score
    ? (match.score.home > match.score.away ? match.awayTeam : match.homeTeam)
    : null;

  const thirdMatch = thirdPlaceData?.data?.rounds?.[0]?.matches?.[0] || null;
  const isThirdCompleted = thirdMatch?.status === 'COMPLETED';
  const thirdWinner = isThirdCompleted && thirdMatch?.score
    ? (thirdMatch.score.home > thirdMatch.score.away ? thirdMatch.homeTeam : thirdMatch.awayTeam)
    : null;

  const getPodiumTeam = (placeKey) => {
    if (placeKey === 'first' && winner) return { name: winner.name, flagUrl: winner.flagUrl };
    if (placeKey === 'second' && loser) return { name: loser.name, flagUrl: loser.flagUrl };
    if (placeKey === 'third' && thirdWinner) return { name: thirdWinner.name, flagUrl: thirdWinner.flagUrl };
    return { name: 'Por definir', flagUrl: null };
  };
  return (
    <div className="final-stage">

      <div className="final-stage__bracket">
        <div className="final-stage__final-title">
          <SectionTitle>{stageName}</SectionTitle>
        </div>
        <div className="final-stage__fecha-row">
          <div className="final-stage__fecha-label">Fecha</div>
          <div className="final-stage__fecha-date">{formatDate(match)}</div>
        </div>
        <div className="final-stage__match">
          <div className="final-stage__connector" aria-hidden="true" />
          <div className="final-stage__connector-2" aria-hidden="true" />
          <div className="final-stage__matches">
            <MatchCard
              time={formatTime(match)}
              teamA={match?.homeTeam?.name || 'Por definir'}
              teamB={match?.awayTeam?.name || 'Por definir'}
              shortNameA={match?.homeTeam?.shortName}
              shortNameB={match?.awayTeam?.shortName}
              flagUrlA={match?.homeTeam?.flagUrl}
              flagUrlB={match?.awayTeam?.flagUrl}
              stadium={match?.venue ? `${match.venue.name}, ${match.venue.city}` : ''}
              status={match?.status}
              score={match?.score}
              className="final-stage__match-card"
              matchMinute={match?.matchProgress?.minute}
              isBroadcastAvailable={match?.broadcast?.isAvailable}
              abbreviateTeamNamesOnMobile
              noFlagPlaceholderSrc={matchCardNoFlagSrc}
              onClick={
                onMatchCardClick && match
                  ? () => handleMatchClick(match)
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      <div className="final-stage__podium">
        <div className="final-stage__podium-title">{translatedTexts['FINAL_TITLE']}</div>
        <div className="final-stage__podium-bars">
          {PODIUM.map(({ label, placeKey, height }) => {
            const rectSrc =
              placeKey === "first"
                ? firstrectangle
                : placeKey === "second"
                  ? secondrectangle
                  : thirdrectangle;

            const shieldSrc = placeKey === "first" ? bigShield : mediumShield;
            const team = getPodiumTeam(placeKey);
            const shortName = team.name !== 'Por definir'
              ? team.name.slice(0, 3).toUpperCase()
              : 'PD';

            return (
            <div
              key={label}
              className={`final-stage__podium-item final-stage__podium-item--${placeKey}`}
            >
              <div className="final-stage__podium-badge">
                {team.flagUrl ? (
                  <img
                    className="final-stage__podium-shield"
                    src={team.flagUrl}
                    alt={team.name}
                  />
                ) : (
                  <img
                    className="final-stage__podium-shield"
                    src={shieldSrc}
                    alt=""
                  />
                )}
              </div>
              <span className="final-stage__podium-team">
                <span className="final-stage__podium-team--full">{team.name}</span>
                <span className="final-stage__podium-team--abbrev">{shortName}</span>
              </span>
              <img
                className="final-stage__podium-rect"
                src={rectSrc}
                alt=""
                style={{ height }}
              />
              <div className="final-stage__podium-label">
              <span >{label}</span>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
